import { useMemo } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useToggle } from 'react-use'
import type RGL from 'react-grid-layout'
import type { AppSetting, SetSettingAppSettingServiceArgs } from '@ztechapps/common.contract'
import { AppSettingScopeNameEnum, DashboardAppSettingKeyEnum } from '@ztechapps/common.contract'
import { setSettingMutation, useAppSetting, useError, useMutation, useUser } from '@ztechapps/common.hook'

export type DashboardSetting = { layout: RGL.Layout[]; toolbox: RGL.Layout[]; isLocked: boolean }

export type UseDashboardSettingProps = { defaultLayout?: DashboardSetting }

export type UseDashboardSettingResult = {
  setting: DashboardSetting
  loading: boolean
  saving: boolean
  save: (next: Partial<DashboardSetting>) => Promise<void>
}

// What a caller that owns no layout (the profile lock toggle) reads when the user has no saved row.
const emptyLayout: DashboardSetting = { layout: [], toolbox: [], isLocked: true }

/**
 * The one read/write path for the signed-in user's dashboard row. Reading never writes: a user with no row sees
 * `defaultLayout` from memory, and the first `save` creates the row. `save` stores only what the row already holds
 * plus `next`, so a caller without the app's layout (the lock toggle) can flip `isLocked` without clobbering it —
 * missing parts fall back to `defaultLayout` on the next read.
 *
 * `loading` is true only until the row first resolves: the list query re-reports `loading` on the refetch that
 * follows every save, which would otherwise unmount a grid mid-interaction.
 */
export const useDashboardSetting = ({
  defaultLayout = emptyLayout,
}: UseDashboardSettingProps = {}): UseDashboardSettingResult => {
  const { user } = useUser()
  const { setting: row, loading } = useAppSetting({
    filter: {
      key: DashboardAppSettingKeyEnum.DASHBOARD_LAYOUT,
      'scope.name': AppSettingScopeNameEnum.user,
      'scope.key': user?._id,
    },
  })
  const [execute, , { loading: saving, error }] = useMutation<AppSetting, SetSettingAppSettingServiceArgs>({
    query: setSettingMutation,
    options: { mutationOptions: { refetchQueries: ['allAppSetting'] } },
  })
  useError(error)

  // Latches on the first resolve and never resets, so the refetch after a save cannot report `loading` again.
  const [resolved, setResolved] = useToggle(false)
  if (!resolved && !loading && user?._id) setResolved(true)

  const saved = useMemo<Partial<DashboardSetting>>(
    () => (isEmpty(row?.value) ? {} : JSON.parse(row.value)),
    [row?.value]
  )
  const setting = useMemo<DashboardSetting>(() => ({ ...defaultLayout, ...saved }), [defaultLayout, saved])

  const save = async (next: Partial<DashboardSetting>) => {
    await execute({
      input: {
        key: DashboardAppSettingKeyEnum.DASHBOARD_LAYOUT,
        value: JSON.stringify({ ...saved, ...next }),
        scopeName: AppSettingScopeNameEnum.user,
        scopeKey: user._id,
      },
    })
  }

  return { setting, loading: !resolved, saving, save }
}
